import type { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import type { CompRef } from '@wix/document-services-types';
import type { ManifestComponentExtensionType } from '@wix/editor-platform-manifest-services';
import type { StyleSDKClient } from '../abstractStyles/types';
import { GlobalDesignStyleEngine } from '../abstractStyles';
import type {
  GlobalDesignPanelProps,
  StyleValues,
  StylesGroup,
} from '../../types';

import type { EditorPointer } from '@wix/editor-document-logic-api';

type SetStyles = (
  editorPointer: EditorPointer<CompRef>,
  type: ManifestComponentExtensionType,
  styleValues: StyleValues,
) => void;

type UpdateSubsetGroupsStylesArgs = {
  subsetGroups: StylesGroup[];
  actionName: StylesDerivedNativeActionNames;
  newStyles: Record<string, string>;
  componentsAPI: GlobalDesignPanelProps['componentsAPI'];
  setStyles: SetStyles;
};

type UpdateSubsetGroupLog = {
  isFailed: boolean;
  editorPointer: EditorPointer<CompRef>;
  styleValues: StyleValues;
};

const toStyleValues = (styles: {
  cssProperties?: Record<string, string>;
  cssCustomProperties?: Record<string, string>;
}): StyleValues => ({
  cssProperties: styles.cssProperties || {},
  cssCustomProperties: styles.cssCustomProperties || {},
});

const hasStyleValues = (styleValues: StyleValues): boolean =>
  Object.keys(styleValues.cssProperties).length > 0 ||
  Object.keys(styleValues.cssCustomProperties).length > 0;

function createSDKClient({
  group,
  setStyles,
}: {
  group: StylesGroup;
  setStyles: SetStyles;
}): StyleSDKClient {
  return {
    getStyleDefinitions: async () =>
      group.context.resolver.getStyleDefinitions(),
    getStyles: async () => group.context.query.getStyles(),
    setStyles: async (styles) => {
      const nextStyleValues = toStyleValues(styles);

      if (!hasStyleValues(nextStyleValues)) {
        return;
      }

      setStyles(
        group.editorPointer,
        group.context.getExtensionType(),
        nextStyleValues,
      );
    },
  };
}

export async function updateSubsetGroupsStyles({
  subsetGroups,
  actionName,
  newStyles,
  componentsAPI,
  setStyles,
}: UpdateSubsetGroupsStylesArgs): Promise<UpdateSubsetGroupLog[]> {
  const logs: UpdateSubsetGroupLog[] = [];

  for (const group of subsetGroups) {
    const sdkClient = createSDKClient({
      group,
      setStyles,
    });

    const engine = GlobalDesignStyleEngine.fromMetadataEngine(
      group.engine,
      actionName,
      sdkClient,
      componentsAPI,
    );

    try {
      await engine.updateProperties(newStyles);
      const appliedStyleValues = await sdkClient.getStyles();

      logs.push({
        isFailed: false,
        editorPointer: group.editorPointer,
        styleValues: appliedStyleValues,
      });
    } catch (_) {
      const appliedStyleValues = await sdkClient.getStyles();

      logs.push({
        isFailed: true,
        editorPointer: group.editorPointer,
        styleValues: appliedStyleValues,
      });
    }
  }

  return logs;
}
